import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UploadDragAndDrop } from '../../../components/common/UploadDragAndDrop/UploadDragAndDrop';
import { AvailableQuotaCard } from '../../../components/common/AvailableQuotaCard/AvailableQuotaCard';
import { ContextApp } from '../../../contexts/ContextApp';
import { notificationContext } from '../../../contexts/NotificationContext';
import useAppSettingService from '../../useAppSettingService';
import {
  DocumentTypeEnum,
  EnumsValues,
  TenantStatus,
} from '../../../enums/EnumsValues';
import { IAppSetting } from '../../../interfaces';
import { Modal } from 'antd';
import { UploadFile } from 'antd/es/upload';
import { RcFile, UploadProps } from 'antd/lib/upload';
import useDocumentService from '../../useDocumentService';
import useNotification from '../../useNotification';

const DEFAULT_MAX_PAGE_PER_DOCUMENT = '6';

interface Props {
  documentType?: DocumentTypeEnum;
  onDocumentUploaded?: () => void;
}

const FileUploader = (props: Props) => {
  const { documentType, onDocumentUploaded } = props;

  const {
    t,
    selectedTenantId,
    getAvailableQuota,
    availableQuota,
    tenantsAssociatedToUser,
    notifications,
  } = useContext(ContextApp);
  const { openNotification } = useContext(notificationContext);

  const { getAppSetting } = useAppSettingService();
  const { createPendingDocument } = useDocumentService();
  const { refreshNotification } = useNotification({});

  const contador = useRef<number>(0);
  const counterProcessedMinusDuplicated = useRef<number>(0);
  const fileLimitExceeded = useRef<boolean>(false);
  const fileQuotaExceeded = useRef<boolean>(false);
  const fileListDuplicatedRef = useRef<any>([]);
  const maxPagePerDocument = useRef<string>(DEFAULT_MAX_PAGE_PER_DOCUMENT);
  const fileListMaxSizeRef = useRef<any>([]);
  const counterFileNotSupported = useRef<number>(0);
  const counterFileNotProcessed = useRef<number>(0);

  const [maxSizeFile, setMaxSizeFile] = useState<number>(
    EnumsValues.SystemLimits.MaxSizeOfFiles,
  );
  const [loadDisabled, setLoadDisabled] = useState<boolean>(false);

  const [fileListState, setFileListState] = useState<any>([]);
  const fileListRef = useRef<any>([]);

  const getLimitMaxSizeFileSetting = async () => {
    try {
      const data: IAppSetting = await getAppSetting(
        EnumsValues.SettingNames.LimitMaxSizeFile,
      );
      setMaxSizeFile(Number(data.setting_value));
    } catch (error) {
      //intentional
    }
  };

  let disabledDragAndDrop =
    availableQuota === 0 ||
    notifications?.quotaIsExpired ||
    loadDisabled === true ||
    tenantsAssociatedToUser.find((tenant) => tenant.id === selectedTenantId)
      ?.tenant_status_id === TenantStatus.Inactiva;

  function mostrarModalDeAdvertencia(filename: string) {
    return new Promise((resolve) => {
      Modal.confirm({
        className: 'modal-duplicated-invoice',
        content: (
          <>
            <div className="ant-modal-confirm-title">
              <p>{t('ModalReceiptDuplicated.duplicatedReceipt')}</p>
            </div>
            <div className="modal-font">
              <p className="modal-description-loadead">
                {t('ModalReceiptDuplicated.duplicatedReceiptDescription')}
              </p>
              <p className="modal-description-filename">{filename}</p>
              <p className="modal-description-ask-reload">
                {t('ModalReceiptDuplicated.duplicatedReceiptAskLoad')}
              </p>
            </div>
          </>
        ),
        okText: t('ModalReceiptDuplicated.reloadFile'),
        cancelText: t('ModalReceiptDuplicated.discardFile'),
        onOk: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        },
        okButtonProps: { className: 'save-btn' },
      });
    });
  }

  const refreshDocumentTable = () => {
    if (onDocumentUploaded) {
      onDocumentUploaded();
    }
  };

  const markFileAsFinishedProcessing = async () => {
    contador.current--;

    if (contador.current === EnumsValues.ConstNumbers.zero) {
      if (
        fileListDuplicatedRef.current.length > EnumsValues.ConstNumbers.zero &&
        selectedTenantId
      ) {
        for (const iterator of fileListDuplicatedRef.current) {
          await mostrarModalDeAdvertencia(iterator.name)
            .then(async (response) => {
              if (response) {
                const responseDuplicated = await createPendingDocument(
                  selectedTenantId,
                  iterator,
                  false,
                  documentType,
                );
                if (responseDuplicated?.filename) {
                  await openNotification({
                    msj: t('UploadDragAndDrop.uploadSuccessful'),
                    filename: responseDuplicated.filename,
                    type: 'success',
                  });
                  refreshNotification();
                  refreshDocumentTable();
                } else {
                  openNotification({
                    msj: responseDuplicated?.message
                      ? responseDuplicated.message
                      : t('error.abm.generalError'),
                    type: 'error',
                  });
                  counterProcessedMinusDuplicated.current--;
                }
              } else {
                counterProcessedMinusDuplicated.current--;
              }
            })
            .catch((error) => {
              console.error(error);
            });
        }
      }
      fileListDuplicatedRef.current = [];
      setLoadDisabled(() => false);
      await getAvailableQuota(selectedTenantId);

      counterProcessedMinusDuplicated.current =
        counterProcessedMinusDuplicated.current -
        fileListMaxSizeRef.current.length -
        counterFileNotProcessed.current;
      fileListMaxSizeRef.current = [];
      counterFileNotProcessed.current = 0;
      const fileNotSupported =
        counterFileNotSupported.current ===
        counterProcessedMinusDuplicated.current;
      counterFileNotSupported.current = EnumsValues.ConstNumbers.zero;

      if (
        counterProcessedMinusDuplicated.current >
          EnumsValues.ConstNumbers.zero &&
        fileLimitExceeded.current === false &&
        fileQuotaExceeded.current === false &&
        fileNotSupported === false
      ) {
        refreshDocumentTable();
      }
      if (fileLimitExceeded.current === true) {
        openNotification({
          msj: t('error.abm.fileLimitExceeded', {
            maxFiles: EnumsValues.SystemLimits.MaxSimultaneousFileUpload,
          }),
          type: 'error',
        });
      } else if (fileQuotaExceeded.current === true) {
        openNotification({
          msj: t('error.abm.fileQuotaExceeded'),
          type: 'error',
        });
      }
    }
  };

  const handleChange = ({
    file,
    fileList,
  }: {
    file: UploadFile;
    fileList: UploadFile<any>[];
  }) => {
    setFileListState(fileList);
    fileListRef.current = fileList;
    fileQuotaExceeded.current = false;
    fileLimitExceeded.current = false;
    if (contador.current !== 0) {
      setLoadDisabled(true);
    }

    if (
      availableQuota &&
      fileList.length >
        Math.min(
          EnumsValues.SystemLimits.MaxSimultaneousFileUpload,
          availableQuota,
        )
    ) {
      markFileAsFinishedProcessing();
      if (
        fileList.length > EnumsValues.SystemLimits.MaxSimultaneousFileUpload
      ) {
        fileLimitExceeded.current = true;
      } else {
        fileQuotaExceeded.current = true;
      }
    } else {
      const fileError = file.status === 'error'; // No need to show success message if all files are with errors

      const fileRemoved = file.status === 'removed'; // No need to show success message if file is removed

      const fileRc = file as RcFile;
      if (fileRc.size > maxSizeFile && !fileRemoved && !fileError) {
        markFileAsFinishedProcessing();
        openNotification({
          msj: t('error.abm.uploadFileFailed'),
          descr: t('error.abm.fileMaxSizeExceeded'),
          filename: file.name,
          type: 'warning',
        });
        fileListMaxSizeRef.current.push(fileRc);
      }
    }
  };

  const uploaderProps: UploadProps<any> = useMemo(
    () => ({
      name: 'file',
      multiple: true,
      listType: 'picture',
      className: 'upload-list-inline',
      fileList: fileListState,

      //MaxCount no es necesario ya que se controla en handleChange
      beforeUpload: (_file, fileList) => {
        setFileListState([]);
        fileListRef.current = [];
        contador.current = fileList.length;
        counterProcessedMinusDuplicated.current = fileList.length;
        if (
          fileList.length >
          Math.min(
            EnumsValues.SystemLimits.MaxSimultaneousFileUpload,
            availableQuota || 0,
          )
        ) {
          return Promise.reject(false);
        }
      },
      accept: '.pdf, .jpg, .jpeg, .png, .tiff, .bmp',

      onChange: ({ file, fileList }) => handleChange({ file, fileList }),
      itemRender: (originNode, file) =>
        file.status === 'error' ? originNode : null,
      customRequest: async (options) => {
        const { file, onSuccess, onError } = options;
        const fileRc = file as RcFile;

        if (
          selectedTenantId &&
          fileRc.size <= maxSizeFile &&
          fileListRef.current.some((item: any) => item.uid === fileRc.uid)
        ) {
          const response = await createPendingDocument(
            selectedTenantId,
            fileRc,
            true,
            documentType,
          );

          if (response?.filename) {
            onSuccess && setTimeout(onSuccess, 0);
            await openNotification({
              msj: t('UploadDragAndDrop.uploadSuccessful'),
              filename: response.filename,
              type: 'success',
            });
            refreshNotification();
            refreshDocumentTable();
          }
          if (
            response?.status_code ===
              EnumsValues.ErrorsStatusCode.duplicatedFileCode &&
            onError
          ) {
            fileListDuplicatedRef.current.push(fileRc);
          } else if (response?.message && onError) {
            if (
              response.status_code ===
              EnumsValues.ErrorsStatusCode.fileNotSupportedCode
            ) {
              counterFileNotSupported.current++;
            } else {
              counterFileNotProcessed.current++;
            }
            openNotification({
              msj: response.message,
              type: 'error',
            });
          }
          markFileAsFinishedProcessing();
          if (
            fileListRef.current[fileListRef.current.length - 1]?.uid ===
            fileRc.uid
          ) {
            setFileListState([]);
            fileListRef.current = [];
          }
        }
      },
      disabled: disabledDragAndDrop,
    }),
    [availableQuota, fileListState, disabledDragAndDrop],
  );

  useEffect(() => {
    getLimitMaxSizeFileSetting();
  }, []);

  return (
    <div className="upload-container">
      <UploadDragAndDrop
        uploadProps={uploaderProps}
        maxPagePerDocument={maxPagePerDocument.current}
      />
      <AvailableQuotaCard version="2" />
    </div>
  );
};

export default FileUploader;
